import { LiveChatConfigPatch, LiveChatSettings } from "types/api"

import { INITIAL_FORM } from "./constants"
import { FormState } from "./types"

/** Blank when unset, so the placeholder can show what the frontend falls back to. */
const numberToField = (value: number | undefined): string => (value === undefined ? "" : String(value))

/** A blank padding clears the stored value so the frontend default applies again. */
const fieldToNumber = (value: string): number | null => {
  const trimmed = value.trim()
  if (trimmed === "") return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

/** A blank text field clears the stored value rather than leaving it untouched. */
const fieldToText = (value: string): string | null => value.trim() || null

export function settingsToForm(settings: LiveChatSettings): FormState {
  const placement = settings.placement ?? {}

  return {
    provider: settings.provider ?? "",
    intercomAppId: settings.intercom?.appId ?? "",
    intercomHashKey: settings.intercom?.hashKey ?? "",
    tawkPropertyId: settings.tawk?.propertyId ?? "",
    tawkWidgetId: settings.tawk?.widgetId ?? "",
    tawkApiKey: settings.tawk?.apiKey ?? "",
    inhouseHost: settings.inhouse?.host ?? "",
    inhousePublicKey: settings.inhouse?.publicKey ?? "",
    inhouseHashSecret: settings.inhouse?.hashSecret ?? "",
    desktopOnly: placement.desktopOnly ?? INITIAL_FORM.desktopOnly,
    hideOnSportsbook: placement.hideOnSportsbook ?? INITIAL_FORM.hideOnSportsbook,
    position: placement.position ?? INITIAL_FORM.position,
    horizontalPadding: numberToField(placement.horizontalPadding),
    sportsbookHorizontalPadding: numberToField(placement.sportsbookHorizontalPadding),
    verticalPadding: numberToField(placement.verticalPadding)
  }
}

/**
 * Sends every field it owns: a value to set it, null to clear it.
 * Secrets are the exception — only sent when the operator typed a new value.
 */
export function buildPayload(form: FormState, loaded: LiveChatSettings | null): LiveChatConfigPatch {
  const secretChanged = (value: string, loadedValue: string | undefined) => value !== "" && value !== (loadedValue ?? "")

  return {
    provider: form.provider === "" ? null : form.provider,
    intercom: {
      appId: fieldToText(form.intercomAppId),
      ...(secretChanged(form.intercomHashKey, loaded?.intercom?.hashKey) && { hashKey: form.intercomHashKey.trim() })
    },
    tawk: {
      propertyId: fieldToText(form.tawkPropertyId),
      widgetId: fieldToText(form.tawkWidgetId),
      ...(secretChanged(form.tawkApiKey, loaded?.tawk?.apiKey) && { apiKey: form.tawkApiKey.trim() })
    },
    inhouse: {
      host: fieldToText(form.inhouseHost.replace(/\/+$/, "")),
      publicKey: fieldToText(form.inhousePublicKey),
      ...(secretChanged(form.inhouseHashSecret, loaded?.inhouse?.hashSecret) && {
        hashSecret: form.inhouseHashSecret.trim()
      })
    },
    placement: {
      desktopOnly: form.desktopOnly,
      hideOnSportsbook: form.hideOnSportsbook,
      position: form.position,
      horizontalPadding: fieldToNumber(form.horizontalPadding),
      sportsbookHorizontalPadding: fieldToNumber(form.sportsbookHorizontalPadding),
      verticalPadding: fieldToNumber(form.verticalPadding)
    }
  }
}
